import { Index, Show } from "solid-js";
import type { Issue } from "../state/validation";
import { useTranslation } from "../state/i18n";
import uuid from "../utils/uuid";
import { isEmpty, isEqual, isNil } from "lodash-es";
import Input from "../components/inputs/Input";
import type { SplitButtonPropertyProps } from "../components/button/SplitButtonProperty";
import SplitButtonProperty from "../components/button/SplitButtonProperty";
import { MAX_PINS } from "../constants";
import InlineMessage from "../components/inlineMessage/InlineMessage";
import { TextAndIconButton } from "../components/button/Button";
import PinProperties from "./PinProperties";
import { getDefaultPinOptions, type Pin } from "../utils/pinUtil";
import Text from "../components/text/Text";

type PinInputProps = {
  pins: Pin[];
  onChange: (pins: Pin[]) => void;
  error: string | undefined;
  nestedErrors: Issue[] | undefined;
};

const PinInput = (props: PinInputProps) => {
  const { t } = useTranslation();

  const handleChange = (id: string, patch: Partial<Pin>) => {
    const next = props.pins.map((p) => (p.id === id ? { ...p, ...patch } : { ...p }));
    props.onChange(next);
  };

  const handleDelete = (id: string) => {
    const next = props.pins.filter((p) => p.id !== id);
    props.onChange(next);
  };

  const handleAdd = () => {
    const next = [
      ...props.pins,
      {
        id: uuid(),
        name: `Pin ${props.pins.length + 1}`,
        icon: { type: "URL", value: "https://cdn.moreapp.com/more-icons/check_green.png" },
        target_form_id: null,
        itemMarkup: [{ type: "literal", value: t(($) => $.FIELD_PROPERTY_PIN_ITEM_MARKUP_DEFAULT_VALUE) }],
      },
    ] satisfies Pin[];
    props.onChange(next);
  };

  const getTrigger = (pin: Pin) => {
    const matchingOption = getDefaultPinOptions(t).find((option) => isEqual(option.value, pin.icon));
    return {
      label: pin.name,
      ariaLabel: t(($) => $.FIELD_PROPERTY_PIN_EDIT_ARIA, { name: pin.name }),
      icon:
        !isNil(matchingOption) && matchingOption.variant === "icon"
          ? { name: matchingOption.icon.name, class: matchingOption.icon.class ?? "" }
          : undefined,
      resourceId: pin.icon?.type === "RESOURCE" ? pin.icon.value : undefined,
    } satisfies SplitButtonPropertyProps["trigger"];
  };

  return (
    <Input
      required
      info={{
        title: t(($) => $.FIELD_PROPERTY_PINS),
        alt: t(($) => $.FIELD_PROPERTY_PINS),
        description: t(($) => $.FIELD_PROPERTY_PINS_DESCRIPTION),
      }}
      label={t(($) => $.FIELD_PROPERTY_PINS)}
      errorMessage={props.error}
    >
      {() => (
        <>
          <ul class="flex flex-col gap-y-1">
            <Index each={props.pins}>
              {(pin, index) => {
                const pinIssues = () =>
                  props.nestedErrors?.filter((issue) => (issue.path ?? []).some((p) => p.key === index));
                return (
                  <li>
                    <SplitButtonProperty
                      ariaLabel={t(($) => $.FIELD_PROPERTY_PIN_POPOVER_TITLE)}
                      Title={() => (
                        <Text
                          as="h3"
                          truncate
                          text={t(($) => $.FIELD_PROPERTY_PIN_POPOVER_TITLE)}
                          class="text-header-md text-primary"
                        />
                      )}
                      hasError={!isEmpty(pinIssues())}
                      trigger={getTrigger(pin())}
                      secondaryAction={{
                        icon: "trash",
                        onClick: () => handleDelete(pin().id),
                        title: t(($) => $.FIELD_PROPERTY_PIN_DELETE),
                      }}
                    >
                      <PinProperties
                        pin={pin()}
                        onChange={(patch) => handleChange(pin().id, patch)}
                        issues={pinIssues()}
                      />
                    </SplitButtonProperty>
                  </li>
                );
              }}
            </Index>
          </ul>

          <Show
            when={props.pins.length < MAX_PINS}
            fallback={
              <InlineMessage variant="warning" label={t(($) => $.FIELD_PROPERTY_PINS_MAX, { max: MAX_PINS })} />
            }
          >
            <TextAndIconButton
              icon="plus"
              text={t(($) => $.FIELD_PROPERTY_PINS_ADD_BUTTON)}
              variant="light"
              onClick={handleAdd}
            />
          </Show>
        </>
      )}
    </Input>
  );
};

export default PinInput;
